import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const PrivacySettings = ({ privacyData, onUpdate }) => {
  const [settings, setSettings] = useState(privacyData);
  const [isSaving, setIsSaving] = useState(false);

  const notificationOptions = [
    { value: 'all', label: 'All Notifications' },
    { value: 'important', label: 'Important Only' },
    { value: 'none', label: 'None' }
  ];

  const profileVisibilityOptions = [
    { value: 'public', label: 'Public' },
    { value: 'friends', label: 'Friends Only' },
    { value: 'private', label: 'Private' }
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      onUpdate(settings);
    } catch (error) {
      console.error('Failed to update privacy settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Privacy & Security</h2>
        </div>
        <Button
          variant="default"
          size="sm"
          iconName="Save"
          iconPosition="left"
          onClick={handleSave}
          loading={isSaving}
        >
          Save Changes
        </Button>
      </div>

      <div className="p-6 space-y-8">
        {/* Profile Visibility */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Profile Visibility</h3>
          <div className="space-y-4">
            <Select
              label="Who can see your profile"
              options={profileVisibilityOptions}
              value={settings.profileVisibility}
              onChange={(value) => handleSettingChange('profileVisibility', value)}
              description="Control who can view your gaming profile and statistics"
            />

            <Checkbox
              label="Show gaming activity"
              description="Display your recently played games and achievements"
              checked={settings.showActivity}
              onChange={(e) => handleSettingChange('showActivity', e.target.checked)}
            />

            <Checkbox
              label="Show online status"
              description="Let others see when you're online and gaming"
              checked={settings.showOnlineStatus}
              onChange={(e) => handleSettingChange('showOnlineStatus', e.target.checked)}
            />

            <Checkbox
              label="Allow friend requests"
              description="Enable other users to send you friend requests"
              checked={settings.allowFriendRequests}
              onChange={(e) => handleSettingChange('allowFriendRequests', e.target.checked)}
            />
          </div>
        </div>

        {/* Data & Analytics */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Data & Analytics</h3>
          <div className="space-y-4">
            <Checkbox
              label="Personalized recommendations"
              description="Use your gaming history to suggest new games you might enjoy"
              checked={settings.personalizedRecommendations}
              onChange={(e) => handleSettingChange('personalizedRecommendations', e.target.checked)}
            />

            <Checkbox
              label="Usage analytics"
              description="Help improve the platform by sharing anonymous usage data"
              checked={settings.usageAnalytics}
              onChange={(e) => handleSettingChange('usageAnalytics', e.target.checked)}
            />

            <Checkbox
              label="Performance data"
              description="Share game performance data to help optimize streaming quality"
              checked={settings.performanceData}
              onChange={(e) => handleSettingChange('performanceData', e.target.checked)}
            />
          </div>
        </div>

        {/* Communication Preferences */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Communication</h3>
          <div className="space-y-4">
            <Select
              label="Email notifications"
              options={notificationOptions}
              value={settings.emailNotifications}
              onChange={(value) => handleSettingChange('emailNotifications', value)}
              description="Choose what email notifications you want to receive"
            />

            <Checkbox
              label="Marketing emails"
              description="Receive updates about new games, features, and special offers"
              checked={settings.marketingEmails}
              onChange={(e) => handleSettingChange('marketingEmails', e.target.checked)}
            />

            <Checkbox
              label="Game updates"
              description="Get notified when your favorite games receive updates"
              checked={settings.gameUpdates}
              onChange={(e) => handleSettingChange('gameUpdates', e.target.checked)}
            />

            <Checkbox
              label="Social notifications"
              description="Receive notifications about friend activity and achievements"
              checked={settings.socialNotifications}
              onChange={(e) => handleSettingChange('socialNotifications', e.target.checked)}
            />
          </div>
        </div>

        {/* Security Quick Links */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Security</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
              <div>
                <h4 className="font-medium text-foreground">Login Notifications</h4>
                <p className="text-sm text-muted-foreground">Get notified of new login attempts</p>
              </div>
              <Checkbox
                checked={settings.loginNotifications}
                onChange={(e) => handleSettingChange('loginNotifications', e.target.checked)}
              />
            </div>
          </div>
        </div>

        {/* Data Export & Deletion */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Data Management</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
              <div>
                <h4 className="font-medium text-foreground">Export Data</h4>
                <p className="text-sm text-muted-foreground">Download a copy of your account data</p>
              </div>
              <Button variant="outline" size="sm" iconName="Download">
                Export Data
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div>
                <h4 className="font-medium text-destructive">Delete Account</h4>
                <p className="text-sm text-muted-foreground">Permanently delete your account and all data</p>
              </div>
              <Button variant="destructive" size="sm" iconName="Trash2">
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;